import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import {
  TRANSACTION_DETAIL_QUERY_KEY,
  TRANSACTION_SUMMARY_QUERY_KEY,
} from "../constants";

import { TRANSACTIONS_LIST_QUERY_KEY } from "./useTransactionsList";

import type { TransactionsListResponse } from "./useTransactionsList";

export interface CorrectCategoryInput {
  slug: string;
  label: string;
}

export interface UseCorrectTransactionCategoryOptions {
  transactionId: string;
  onError?: () => void;
}

type TaxonomyField = "category" | "sub_category";

const taxonomyMutationKey = (transactionId: string) => [
  "correct-transaction-taxonomy",
  transactionId,
];

export const useIsCorrectingTaxonomy = (transactionId: string) =>
  useIsMutating({ mutationKey: taxonomyMutationKey(transactionId) }) > 0;

const patchFor = (field: TaxonomyField, label: string) =>
  field === "category"
    ? { category: label, sub_category: "" }
    : { sub_category: label };

const applyCorrection = (
  response: TransactionsListResponse | undefined,
  field: TaxonomyField,
  transactionId: string,
  label: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === transactionId
        ? { ...transaction, ...patchFor(field, label) }
        : transaction
    ),
  };
};

const restoreCorrection = (
  response: TransactionsListResponse | undefined,
  field: TaxonomyField,
  previous: API.Transaction,
  optimisticLabel: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === previous.id && transaction[field] === optimisticLabel
        ? {
            ...transaction,
            category: previous.category,
            sub_category: previous.sub_category,
          }
        : transaction
    ),
  };
};

/**
 * Corrects one taxonomy field on a transaction, optimistically.
 *
 * Shared rather than copied per field: the cancel, snapshot and restore rules below are what
 * keep two in-flight corrections from undoing each other, and a second copy would drift.
 */
const useCorrectTransactionTaxonomy = (
  field: TaxonomyField,
  { transactionId, onError }: UseCorrectTransactionCategoryOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: taxonomyMutationKey(transactionId),
    mutationFn: ({ slug }: CorrectCategoryInput) =>
      ApiHttp.fetch(
        `transactions/${transactionId}`,
        { method: "PUT" },
        { [field]: slug }
      ),

    onMutate: async ({ label }: CorrectCategoryInput) => {
      await queryClient.cancelQueries({
        queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
      });
      await queryClient.cancelQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      });

      const previousRows = queryClient
        .getQueriesData<TransactionsListResponse>({
          queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
        })
        .flatMap(([key, response]) => {
          const row = response?.transactions.find(
            (transaction) => transaction.id === transactionId
          );
          return row ? [[key, row] as const] : [];
        });

      queryClient.setQueriesData<TransactionsListResponse>(
        { queryKey: [TRANSACTIONS_LIST_QUERY_KEY] },
        (response) => applyCorrection(response, field, transactionId, label)
      );

      return { previousRows };
    },

    onError: (_error, { label }: CorrectCategoryInput, context) => {
      context?.previousRows.forEach(([key, row]) => {
        queryClient.setQueryData<TransactionsListResponse>(key, (response) =>
          restoreCorrection(response, field, row, label)
        );
      });
      onError?.();
    },

    // Query-managed, not a side fetch: cancelQueries above cancels a stale one from an earlier
    // settle, and returning the promise keeps isPending true until the panel is actually fresh.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
        }),
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_SUMMARY_QUERY_KEY],
        }),
      ]),
  });
};

export const useCorrectTransactionCategory = (
  options: UseCorrectTransactionCategoryOptions
) => useCorrectTransactionTaxonomy("category", options);

export const useCorrectTransactionSubCategory = (
  options: UseCorrectTransactionCategoryOptions
) => useCorrectTransactionTaxonomy("sub_category", options);
